// The companies a Nova admin can attach a show to.
//
// ---------------------------------------------------------------------------
// THE SAME LIST OPEN A SHOW ALREADY READS
//
// `GET /v1/admin/clients` is one endpoint. This page lists it; Open a show
// offers it as a dropdown. A client created on either screen is on the next
// read of the other, because there is no second directory to drift. A list
// this file invented, or a cache it kept, would be a second opinion about
// whose companies exist.
//
// ---------------------------------------------------------------------------
// ONE NAME, AND THE AGREEMENT
//
// 010 put one `name` column on `client` and refused a legal name beside a
// trading name: two columns with no rule about which a screen prints is two
// names that can disagree in front of the person they describe. That still
// holds, and correcting the name is the write 010 allowed UPDATE for.
//
// 126 added the second thing this page stores, and it is not a convenience
// field: the processing agreement is what permits an export to run at all, so
// recording it here is the act that unlocks producing a file of this client's
// attendees. The retention period and the contract contacts come with it,
// because an agreement that does not say how long data is kept is not one.
//
// A logo, brand colours and a brand font are named so the page is the place
// they will land, and each card says they cannot be set here yet. A disabled
// control that looks like it works is how a client concludes the row is
// configured when it is not — Registration.tsx's argument, applied to a
// company.
//
// Theme, when it arrives, is a closed token set signed into an activation
// bundle and contrast-checked (`xp/ui/src/theme.ts`). It is not a hex picker
// on this row. The desk stays Nova's colours regardless.
//
// ---------------------------------------------------------------------------
// THE COUNTS, AND WHY THIS FILE STOPPED REFUSING THEM
//
// This header used to say NO COUNTS, and the argument was a good one: how many
// shows a client has and how many attendees are headlines each report records
// against its own activation, so printing them on one page hands over every
// client's figures against a single audit row saying somebody listed companies.
//
// The answer to that was not to keep the figures off the page. It was to make
// the record match the disclosure — `GET /v1/admin/clients` now files a
// report_access row of its own, naming who asked and how many companies were
// named. What the old rule was protecting is protected by the audit; what it
// was costing was a directory nobody could use.
//
// The figures are still the service's. This screen prints what the endpoint
// sent and computes nothing: see the note on Registrations, which is a sum of
// per-show counts and is labelled as one because Nova cannot tell that somebody
// at two of a client's shows is one person.
//
// ---------------------------------------------------------------------------
// ADVERTISED, NOT GATED
//
// The rail hides this from a client viewer. The route does not. A deep link
// reaches the service, and the service's own sentence is what they read.
//
// ---------------------------------------------------------------------------
// PORTED AGAINST handoff/console-clients.jsx, STRUCTURALLY AND NOT BY RENAME
//
// The design's two components are ClientsScreen and ClientScreen, and this file
// is built on their skeletons rather than on the console's older `.console-*`
// furniture. That distinction is the whole of the exercise: the console pins
// `data-system="charcoal"` and the `--d-*` values are hex-identical to the `--xp-*`
// ones, so swapping a token name changes nothing anybody can see. What was
// wrong here was the shapes.
//
// WHAT MOVED. The list is PageHead, a Find one panel built on FieldRow, and the
// design's six-column table inside .tablewrap/.tablescroll with a data-label on
// every cell, so it folds into stacked blocks below 760px instead of scrolling
// sideways. The company's page is the design's order: back, head, the warning,
// The record as a two-column .dl, Their activations, What may be produced, and
// then the four things this console can write. Each of those four was a
// `.panel` with an `<h2 className="panel-h">` inside it and is now a `<section>`
// with a SecLabel over it, which is the design's own rhythm and is what makes
// the page scan as one record rather than as a column of cards.
//
// WHAT CAME OFF. PageHead's `uuid` block, which drew a bare panel headed
// ACTIVATION UUID over a company's page — the wrong word for the identifier and
// the wrong furniture for a record. The client id is a row of The record now,
// which is where the design puts it.
//
// WHAT THE DESIGN HAS AND THIS DOES NOT is a "client since" date and a count of
// people held. Neither exists to read. Their activations does: the directory
// already names the company on each show this sign-in may open, so this page
// filters that list and says so when the count above is a different number.

import { Crumbs, DefList, NoticeBox, PageHead } from "@nova/desk-ui/parts";
import { Empty, FieldRow, SecLabel } from "@nova/desk-ui/kit";
import { DeskPage, SortHead, useListSort, type ListSort } from "@nova/desk-ui/page";
import { Confirm, Failed, ListState, Loading } from "@nova/desk-ui/states";
import { useEffect, useState, type ReactNode } from "react";
import type { ActivationDirectory, ActivationLine } from "../../../operator/src/activations.js";
import type { ClientSortKey } from "../../../api/src/admin/endpoints.js";
import {
  archiveClient,
  correctClient,
  correctIdentity,
  createClient,
  createIdentity,
  listClients,
  listIdentities,
  recordAgreement,
  type AdminRefusal,
  type ClientAgreement,
  type LiveClient,
  type LiveIdentity,
} from "../api/admin.js";
import { readActivations, type ApiOptions, type Failure } from "../api/client.js";
import type { SessionToken } from "../session/store.js";
import { activationName } from "./activationName.js";
import { AdminRefused } from "./Authorise.js";
import { Dialog } from "./Dialog.js";
import { STATE_TAG, lifecycle } from "./lifecycle.js";

export type ClientsProps = {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  /** Null on the list. Set on one company. */
  readonly clientId: string | null;
  readonly onOpen: (clientId: string) => void;
  /** Open one of this company's shows. Same destination the directory uses. */
  readonly onOpenActivation: (activationId: string) => void;
  readonly onList: () => void;
  readonly onSessionEnded: (detail: string) => void;
};

type Listed =
  | { readonly status: "reading" }
  | { readonly status: "read"; readonly clients: readonly LiveClient[] }
  | { readonly status: "failed"; readonly refusal: AdminRefusal };

/**
 * The design's own lede, which is truer than the one this screen had.
 *
 * A constant because the reading state, the refused state and the list all head
 * the same page and a page that renames itself while it loads is a page that
 * looks like it navigated.
 */
const LIST_LEDE =
  "Clients this account can access. Each client controls the personal data collected at its activations.";

/**
 * The list's order before anybody presses a head, which is the service's own:
 * A to Z by name. Named here so the heads can tell a screen reader so.
 */
const CLIENTS_REST: ListSort<ClientSortKey> = { key: "name", dir: "asc" };

/**
 * Which way a column runs the first time its head is pressed: the figures
 * biggest first, because that is what a reader sorts a count to find. Name,
 * retention (shortest kept first) and agreement (missing first, which is the
 * list somebody is chasing) start ascending.
 */
const CLIENTS_FIRST: Readonly<Partial<Record<ClientSortKey, "asc" | "desc">>> = {
  activations: "desc",
  registrations: "desc",
};

export function Clients({
  api,
  token,
  clientId,
  onOpen,
  onOpenActivation,
  onList,
  onSessionEnded,
}: ClientsProps): ReactNode {
  const [listed, setListed] = useState<Listed>({ status: "reading" });
  const [generation, setGeneration] = useState(0);
  // THE ORDER IS THE SERVICE'S, ASKED FOR AND NOT APPLIED HERE (12 Oct
  // hand-off: every column sorts, on the server). Held above the list rather
  // than in it, so the re-read after adding a company keeps the order the
  // reader chose instead of dropping back to A to Z.
  const order = useListSort<ClientSortKey>(CLIENTS_REST, CLIENTS_FIRST);
  const chosen = order.lit ? order.sort : null;
  const chosenKey = chosen?.key ?? null;
  const chosenDir = chosen?.dir ?? null;

  useEffect(() => {
    let cancelled = false;
    // The skeleton on the first read only. A re-read — after an add, or for a
    // new order — keeps the rows on screen until the answer lands, so pressing
    // a head does not blank the page and put the search box back under a
    // loading state the reader did not ask for.
    setListed((now) => (now.status === "read" ? now : { status: "reading" }));

    void (async () => {
      const result = await listClients(
        api,
        token,
        chosenKey === null || chosenDir === null ? null : { key: chosenKey, dir: chosenDir },
      );
      if (cancelled) return;

      if (result.ok) {
        setListed({ status: "read", clients: result.value.clients });
        return;
      }

      setListed({ status: "failed", refusal: result });
      if (result.kind === "session_ended") onSessionEnded(result.detail);
    })();

    return () => {
      cancelled = true;
    };
  }, [api, token, onSessionEnded, generation, chosenKey, chosenDir]);

  // THE DESIGN'S READING STATE, WHICH IS ROWS AND NOT A SENTENCE. `Loading`
  // draws the shape of the answer, so the page does not jump when it lands.
  // Six columns because that is what the table below has; a skeleton with the
  // wrong number of them is the jump it exists to prevent.
  // THE DESIGN'S READING STATE, AND ITS FAILED ONE, DRAWN IN THE PAGE
  // (console-clients.jsx, ListState): the head, Add a client and Find a
  // client stay, and only the answer under "Clients" is the skeleton, or the
  // kit's Failed with Ask again. The record page keeps its own head while the
  // list it is read from loads.
  if (listed.status === "reading" && clientId !== null) {
    return (
      <DeskPage
        head={<PageHead title="This client" lede="Loading the client record…" explain="inline" />}
      />
    );
  }

  const retry = (): void => {
    setListed({ status: "reading" });
    setGeneration((n) => n + 1);
  };

  if (clientId === null && (listed.status === "reading" || (listed.status === "failed" && listed.refusal.kind !== "not_permitted"))) {
    return (
      <List
        clients={null}
        answer={
          listed.status === "reading" ? (
            // Six columns because that is what the table below has.
            <Loading rows={5} cols={6} label="Loading clients…" />
          ) : (
            <Failed
              what="Could not load clients"
              detail={listed.refusal.detail}
              onRetry={listed.refusal.kind === "session_ended" ? undefined : retry}
            />
          )
        }
        order={order}
        api={api}
        token={token}
        onOpen={onOpen}
        onSessionEnded={onSessionEnded}
        onAdded={() => setGeneration((n) => n + 1)}
      />
    );
  }

  // A REFUSAL BY POLICY IS THE WHOLE ANSWER, and stays the whole page: the
  // router only draws this page for an Admin, so a 403 here is the service
  // saying this sign-in may not list companies after all (a token rather than
  // a person, say), and a form for creating one over that sentence would be a
  // control that always refuses.
  if (listed.status !== "read") {
    return listed.status === "failed" ? (
      <div className="stack">
        <PageHead title="Clients" lede={LIST_LEDE} explain="info" />
        <AdminRefused refusal={listed.refusal} />
      </div>
    ) : null;
  }

  if (clientId !== null) {
    const client = listed.clients.find((row) => row.clientId === clientId);
    return (
      <One
        client={client ?? null}
        clientId={clientId}
        api={api}
        token={token}
        onList={onList}
        onOpenActivation={onOpenActivation}
        onSessionEnded={onSessionEnded}
        onCorrected={() => setGeneration((n) => n + 1)}
        onArchived={() => {
          setGeneration((n) => n + 1);
          onList();
        }}
      />
    );
  }

  return (
    <List
      clients={listed.clients}
      answer={null}
      order={order}
      api={api}
      token={token}
      onOpen={onOpen}
      onSessionEnded={onSessionEnded}
      onAdded={() => setGeneration((n) => n + 1)}
    />
  );
}

function List({
  clients,
  answer,
  order,
  api,
  token,
  onOpen,
  onSessionEnded,
  onAdded,
}: {
  /** Null while the list is read or after it failed, and `answer` stands in for it. */
  readonly clients: readonly LiveClient[] | null;
  readonly answer: ReactNode;
  readonly order: {
    readonly sort: ListSort<ClientSortKey>;
    readonly lit: boolean;
    readonly onSort: (key: ClientSortKey) => void;
  };
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly onOpen: (clientId: string) => void;
  readonly onSessionEnded: (detail: string) => void;
  readonly onAdded: () => void;
}): ReactNode {
  const [find, setFind] = useState("");

  // Narrowing what is drawn, out of rows the server has already sent. No second
  // request goes out and the order is untouched — the same line Choose.tsx
  // draws between "what may be seen", which is the server's, and "what is drawn
  // right now", which is the reader's. Name and identifier only: those are the
  // two things somebody has in front of them when they come looking, and they
  // are the two things the row draws. The design searches an address as well,
  // which is a column this list does not have.
  const term = find.trim().toLowerCase();
  const all = clients ?? [];
  const shown =
    term === ""
      ? all
      : all.filter((client) =>
          `${client.name} ${client.clientId}`.toLowerCase().includes(term),
        );

  // How many companies nothing may be produced for. Counted over every client
  // rather than over the filtered rows, because it is a fact about the book of
  // business and not about the search — a reader who has typed three letters
  // still wants to know how many are outstanding.
  //
  // Only "none" counts. A grandfathered client can be exported for, so counting
  // it here would put a number beside a sentence that says nothing may be
  // produced for them, which is false.
  const unagreed = all.filter((client) => client.agreement.state === "none").length;

  // THE 12 OCTOBER PAGE SHAPE. Find a client is the right-hand column (the
  // hand-off's README: "Clients' search is in the right-hand column"), pinned
  // beside the list from 1100px and above it under that; the title and Add a
  // client span the page over both. DeskPage draws the columns; nothing here
  // measures anything.
  return (
    <DeskPage
      head={
        <>
          <PageHead title="Clients" lede={LIST_LEDE} explain="info" />
          <AddClient api={api} token={token} onSessionEnded={onSessionEnded} onAdded={onAdded} />
        </>
      }
      side={
        <section>
          <SecLabel>Find a client</SecLabel>
          <div className="panel panel-stack">
            <FieldRow label="Search" help="Search by client name or ID.">
              <input
                className="input"
                type="search"
                value={find}
                onChange={(event) => setFind(event.target.value)}
                placeholder="Name or identifier"
              />
            </FieldRow>

            {/* A count of the rows in front of the reader, which is a different
                thing from the per-client figures this file's header refuses. That
                rule is about how many shows or how many people a named company
                has — a headline recorded against its own activation's audit row,
                handed over here against one line saying somebody listed
                companies. Saying how many rows arrived discloses nothing that the
                rows themselves do not. The totals are the column's, as the
                hand-off puts them: search, filters, sort, export and totals. */}
            {clients === null ? null : (
              <p className="help help-tight">
                {clients.length} {clients.length === 1 ? "client" : "clients"}
                {term === "" ? "" : ` · ${shown.length} matching`}
                {unagreed === 0 ? "" : ` · ${unagreed} without a processing agreement`}
              </p>
            )}
          </div>
        </section>
      }
    >
      {/* THE CAPTION IS NOT DECORATION AND MUST NOT BE DROPPED. The design's
          column is "People held", counted once per address across everything a
          client owns. Nova cannot answer that: an attendee reference is unique
          inside one activation and never across one, so the same person at two
          of a client's shows is two references and there is nothing to join
          them on. What is countable is a registration per show, added up — so
          that is what is counted and what it is called. A column headed with a
          person and filled with an attendance would be right for most clients
          and wrong for exactly the ones with a following, which is the worst
          way for a number to be wrong. */}

      <section>
        <SecLabel info="Registrations are counted once per person per activation. Someone who attended two activations is counted twice.">
          {clients === null ? "Clients" : `Clients · ${shown.length}`}
        </SecLabel>

        {clients === null ? answer : (
        <ListState
          state="ready"
          rows={shown}
          cols={6}
          unit="clients"
          empty={
            all.length === 0 ? (
              <Empty title="No clients yet">
                <p>Add a client before creating its first activation.</p>
              </Empty>
            ) : (
              <Empty title="No clients match">
                <p>Try another client name or ID.</p>
              </Empty>
            )
          }
        >
          <div className="tablewrap">
            <div className="tablescroll">
              <table>
                <thead>
                  {/* Every column sorts, and the service does the sorting: a
                      head asks for an order and the rows arrive in it, so the
                      order covers the whole list rather than what is drawn.
                      The keys are CLIENT_SORT_KEYS in the service's
                      endpoints.ts, which refuses any other; Open is a control,
                      not a column, and has nothing to order by. */}
                  <tr>
                    <SortHead of="name" {...order}>
                      Client
                    </SortHead>
                    <SortHead of="activations" {...order} className="num">
                      Activations
                    </SortHead>
                    <SortHead of="registrations" {...order} className="num">
                      Registrations
                    </SortHead>
                    <SortHead of="retention" {...order}>
                      Retention
                    </SortHead>
                    <SortHead of="agreement" {...order}>
                      Agreement
                    </SortHead>
                    <th scope="col">
                      <span className="vh">Open</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((client) => (
                    // The design's whole-row open: the row is the target, by
                    // pointer or by Enter, and Open at its end is the same
                    // thing for a reader who looks for a button.
                    <tr
                      key={client.clientId}
                      className="row-go"
                      tabIndex={0}
                      onClick={() => onOpen(client.clientId)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" && event.target === event.currentTarget) onOpen(client.clientId);
                      }}
                    >
                      <td data-label="Client">
                        <b>{client.name}</b>
                        {/* The design's second line here is "Client since
                            <date>", which nothing sends. The identifier is what
                            is real, and it is also what the search reads, so
                            the row shows what a reader can type. */}
                        <p className="t-note id clients-uuid mono">{client.clientId}</p>
                      </td>
                      <td data-label="Activations" className="num">
                        <Figure of={client.activations} />
                        {client.open === null ? null : (
                          <div className="t-note">{client.open} open</div>
                        )}
                      </td>
                      <td data-label="Registrations" className="num">
                        <Figure of={client.registrations} />
                      </td>
                      <td data-label="Retention">
                        <Retention of={client.agreement} />
                      </td>
                      <td data-label="Agreement">
                        <Agreement of={client.agreement} />
                      </td>
                      <td data-label="">
                        <button
                          type="button"
                          className="btn btn-quiet btn-sm"
                          onClick={(event) => {
                            event.stopPropagation();
                            onOpen(client.clientId);
                          }}
                        >
                          Open
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </ListState>
        )}

        {/* Outside the table's frame rather than inside it, because the pack's
            wrapper clips to its own radius and a paragraph tucked under the last
            row reads as a row. */}
      </section>
    </DeskPage>
  );
}

/**
 * A count, or the fact that there is not one.
 *
 * `.unk` and a word, not a zero and not a dash. The service always sends these;
 * a null means this bundle is talking to one that predates them, which happens
 * for the length of a deploy because the console and the API go out separately.
 * Zero would be a wrong number with nothing on the screen admitting it, and the
 * em dash this used to draw was a mark a reader has to be taught. The design's
 * own class for a value that is unknown rather than nought is what draws it.
 */
function Figure({ of }: { readonly of: number | null }): ReactNode {
  if (of === null) return <span className="unk">not sent</span>;
  return <>{of.toLocaleString()}</>;
}

/**
 * How long the agreement says data is kept.
 *
 * "Not agreed" is a warning tag rather than a quiet word, because it is the same
 * fact as the Agreement column beside it seen from the other side: no agreement,
 * no retention, and nothing may be produced. The design draws it that way and it
 * is the right emphasis.
 */
function Retention({ of }: { readonly of: ClientAgreement }): ReactNode {
  if (of.state === "unknown") return <span className="unk">not sent</span>;
  if (of.retentionMonths === null) return <span className="tag tag-warn">not agreed</span>;
  return <>{monthsRead(of.retentionMonths)}</>;
}

/**
 * Whether a file may be produced for this client, and on what basis.
 *
 * THREE OUTCOMES AND NOT TWO, which is the whole reason 126 kept grandfathering
 * in its own column. "Signed" is a document somebody put on file. "On file
 * before records" is a client that predates the rule and is permitted on that
 * basis alone — permitted, and still owing a real agreement, which is a thing
 * this column has to be able to say out loud. Drawing it the same as a signed
 * one would hide exactly the list somebody will eventually be asked for.
 *
 * It is not a warning, because nothing is wrong: the export runs. A quiet tag
 * is the honest weight.
 */
function Agreement({ of }: { readonly of: ClientAgreement }): ReactNode {
  switch (of.state) {
    case "recorded":
      return <>{of.signedOn === null ? "signed" : `signed ${dayRead(of.signedOn)}`}</>;
    case "grandfathered":
      return <span className="tag">on file before records</span>;
    case "none":
      return <span className="tag tag-warn">missing</span>;
    case "unknown":
      // The service did not send one, which is not the same as there not being
      // one. Figure's argument: a bundle talking to an API that predates the
      // field must not report every client as having no paperwork.
      return <span className="unk">not sent</span>;
  }
}

/** A period as somebody would say it, rather than as it is stored. */
function monthsRead(months: number): string {
  if (months === 12) return "12 months";
  if (months % 12 === 0) return `${months} months`;
  return `${months} ${months === 1 ? "month" : "months"}`;
}

/**
 * A date as a person reads it.
 *
 * Parsed as a plain calendar day and formatted in UTC. The service sends
 * `YYYY-MM-DD` and means the day on the document; letting the browser apply a
 * local offset would show a signature dated the 11th as the 10th to anybody west
 * of Greenwich, which is a wrong date on a legal record.
 */
function dayRead(day: string): string {
  const parsed = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return day;
  return parsed.toLocaleDateString(undefined, { timeZone: "UTC", day: "numeric", month: "short", year: "numeric" });
}

function AddClient({
  api,
  token,
  onSessionEnded,
  onAdded,
}: {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly onSessionEnded: (detail: string) => void;
  readonly onAdded: () => void;
}): ReactNode {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<AdminRefusal | null>(null);
  const [added, setAdded] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    setBusy(true);
    setRefusal(null);

    const result = await createClient(api, token, name);
    setBusy(false);

    if (result.ok) {
      setAdded(result.value.name);
      setName("");
      onAdded();
      return;
    }

    setRefusal(result);
    if (result.kind === "session_ended") onSessionEnded(result.detail);
  };

  // A SECTION WITH A LABEL OVER IT, AND THE DESIGN HAS NO SUCH PANEL AT ALL.
  // Its Clients screen cannot create a company, so there was nothing to port
  // the shape of; it is dressed in the design's own idiom for a panel of
  // controls — SecLabel above, panel-stack inside — rather than being left as
  // the one card on the page with its heading inside it.
  return (
    <section>
      <SecLabel info="This name appears across Console and on exports.">
        Add a client
      </SecLabel>
      <div className="panel panel-stack">
        {added === null ? null : (
          <div className="notice notice-good" role="status">
            <h3>{added} added</h3>
            <p>The client is now available when you add an activation.</p>
          </div>
        )}
        {refusal === null ? null : <AdminRefused refusal={refusal} />}
        <FieldRow label="The client&rsquo;s name">
          <input
            className="input"
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={busy}
          />
        </FieldRow>
        <div className="acts acts-flush">
          <button type="button" className="btn" onClick={() => void submit()} disabled={busy}>
            {busy ? "Adding…" : "Add this client"}
          </button>
        </div>
      </div>
    </section>
  );
}

/**
 * The design's lede for one company, with the half nothing sends left out.
 *
 * ClientScreen writes "Client since 4 March 2025. 2 activations on this
 * service." The first sentence has no field behind it: `client` carries a name
 * and an identifier and no created date, and the listing does not send one. So
 * the count is drawn, from the figure the service sent, and the date is not
 * invented from the first activation or from anything else adjacent to it.
 */
function ledeFor(client: LiveClient | null): string {
  const tail = "Manage the client record, agreement and contacts.";
  if (client === null || client.activations === null) return tail;
  const many = client.activations === 1 ? "activation" : "activations";
  return `${client.activations} ${many}. ${tail}`;
}

function One({
  client,
  clientId,
  api,
  token,
  onList,
  onOpenActivation,
  onSessionEnded,
  onCorrected,
  onArchived,
}: {
  readonly client: LiveClient | null;
  readonly clientId: string;
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly onList: () => void;
  readonly onOpenActivation: (activationId: string) => void;
  readonly onSessionEnded: (detail: string) => void;
  readonly onCorrected: () => void;
  readonly onArchived: () => void;
}): ReactNode {
  return (
    <div className="stack">
      {/* Above the title, which is the design's, and it reads better than it
          did below: somebody who followed a link into the wrong company wants
          the way out before they have finished reading whose record this is.
          Breadcrumbs since the October hand-off, Admin's, in place of the
          "← Back to clients" button: the way out and where you are, in one
          line. */}
      <Crumbs items={[["Clients", onList], [client === null ? "Client" : client.name, null]]} />

      {/* NO `uuid`, WHICH IS THE ONE THING THIS PAGE HAD THAT THE DESIGN DOES
          NOT. PageHead's block is headed ACTIVATION UUID, and this is a client:
          the word was wrong, and a bare identifier in a panel of its own over a
          record is furniture doing nothing. The design puts the identifier in
          The record, as a row, and so does this. */}
      <PageHead title={client === null ? "This client" : client.name} lede={ledeFor(client)} explain="info" />

      {client === null ? (
        <NoticeBox kind="quiet" title="Client not found">
          <p>Check the link or ask Nova to grant this account access.</p>
          <p className="id clients-uuid">{clientId}</p>
        </NoticeBox>
      ) : (
        <>
          {/* THE WARNING COMES BEFORE THE RECORD, because it changes what the
              rest of the page means. A reader looking at activations and
              registrations on a client nothing may be produced for should know
              that before they go looking for the export. */}
          {client.agreement.state !== "none" ? null : (
            <NoticeBox kind="amber" title="Add a processing agreement">
              <p>
                Registration setup can continue, but personal-data exports remain unavailable.
              </p>
              {/* THE SECOND CONSEQUENCE, ADDED WHEN IT BECAME ONE. Until the
                  unverified attempts table existed, no agreement meant only that
                  no file could be produced, which is a refusal and therefore
                  safe. It now also means an absent retention period, and the
                  purge reads that column: with it null, only sealing the show
                  removes those addresses, and nothing seals a show on a timer.
                  So the one case where a missing agreement quietly keeps data
                  rather than quietly refusing to hand it over is said here,
                  beside the refusal, rather than left in a migration header. */}
              <p>
                Without a retention period, unverified email addresses are held until the activation
                is sealed. Recording the agreement applies its period to existing addresses too.
              </p>
            </NoticeBox>
          )}

          <ClientRecord client={client} />
          <TheirActivations
            client={client}
            api={api}
            token={token}
            onOpen={onOpenActivation}
            onSessionEnded={onSessionEnded}
          />
          <MayBeProduced client={client} />

          <Agreed
            client={client}
            api={api}
            token={token}
            onSessionEnded={onSessionEnded}
            onRecorded={onCorrected}
          />
          <CorrectName
            client={client}
            api={api}
            token={token}
            onSessionEnded={onSessionEnded}
            onCorrected={onCorrected}
          />
          <People
            clientId={client.clientId}
            api={api}
            token={token}
            onSessionEnded={onSessionEnded}
          />
          <Archive
            client={client}
            api={api}
            token={token}
            onSessionEnded={onSessionEnded}
            onArchived={onArchived}
          />
        </>
      )}

      <NotOnThisRow />
    </div>
  );
}

/**
 * The record, which is the design's two-column definition block.
 *
 * SIX OF THE DESIGN'S SEVEN ROWS ARE REAL AND ONE IS NOT. Client id, named
 * contact, address for notices, retention period and processing agreement all
 * exist: the last four are 126's, on the agreement rather than on the company,
 * and they are drawn here because a reader looking for "who do we write to" does
 * not care which table it is stored in.
 *
 * ITS SEVENTH ROW IS "PEOPLE HELD", AND IT IS THE ONE THING ON THIS SCREEN THAT
 * CANNOT BE ANSWERED. It is a count of addresses across everything a client
 * owns, counted once each. An attendee reference is unique inside one activation
 * and never across one, so the same person at two of a client's shows is two
 * references with nothing to join them on. What the service does send is a sum
 * of per-show registrations, which is a different number, so that is what is
 * drawn and what it is called. The paragraph under the block is the difference,
 * and it is load-bearing rather than decorative.
 */
function ClientRecord({ client }: { readonly client: LiveClient }): ReactNode {
  const { agreement } = client;
  return (
    <section>
      <SecLabel info="Registrations are counted once per person per activation. A person at two activations is counted twice. The retention period currently applies only to unverified email addresses.">
        Client record
      </SecLabel>
      <DefList
        rows={[
          ["Client id", <span className="clients-uuid">{client.clientId}</span>],
          ["Named contact", agreement.noticesPerson ?? <span className="quiet">not recorded</span>],
          [
            "Address for notices",
            agreement.noticesEmail ?? <span className="quiet">not recorded</span>,
          ],
          ["Retention period", <Retention of={agreement} />],
          ["Processing agreement", <Agreement of={agreement} />],
          [
            "Activations",
            <>
              <Figure of={client.activations} />
              {client.open === null ? null : <span className="quiet"> · {client.open} open</span>}
            </>,
          ],
          ["Registrations", <Figure of={client.registrations} />],
        ]}
      />
      {/* THE DESIGN SAYS THE RETENTION PERIOD IS WHAT THE SERVICE DELETES ON.
          IT IS ALMOST ENTIRELY NOT, AND THIS SENTENCE IS THE DIFFERENCE.
          The wording now lives on the section's i: a paragraph under the
          figures used to be read as the figures themselves. */}
    </section>
  );
}

/**
 * The shows this sign-in may open for this company.
 *
 * `GET /v1/admin/clients` still sends how many, not which. The rows come from
 * the same directory Choose.tsx already reads: every activation the credential
 * may see, filtered here by `client.client_id`. That list is scoped by 004's
 * policies rather than by ownership, so it can be shorter than the count above.
 * When those two numbers differ the page says so, instead of quietly drawing a
 * table that disagrees with The record.
 */
type TheirShows =
  | { readonly status: "reading" }
  | { readonly status: "read"; readonly activations: readonly ActivationLine[] }
  | { readonly status: "failed"; readonly failure: Failure };

function TheirActivations({
  client,
  api,
  token,
  onOpen,
  onSessionEnded,
}: {
  readonly client: LiveClient;
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly onOpen: (activationId: string) => void;
  readonly onSessionEnded: (detail: string) => void;
}): ReactNode {
  const [listed, setListed] = useState<TheirShows>({ status: "reading" });

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const result = await readActivations<ActivationDirectory>(api, token);
      if (cancelled) return;
      if (!result.ok) {
        setListed({ status: "failed", failure: result });
        if (result.kind === "session_ended") onSessionEnded(result.detail);
        return;
      }
      setListed({
        status: "read",
        activations: (result.value.activations ?? []).filter((row) => row.client.client_id === client.clientId),
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [api, token, client.clientId, onSessionEnded]);

  const named = client.activations;
  const visible = listed.status === "read" ? listed.activations.length : null;
  const disagree = named !== null && visible !== null && named !== visible;

  return (
    <section>
      <SecLabel>Activations</SecLabel>
      {listed.status === "reading" ? <Loading rows={3} cols={5} label="Loading activations…" /> : null}
      {listed.status === "failed" ? (
        <Failed what="Could not load activations" detail={listed.failure.detail} />
      ) : null}
      {listed.status === "read" && listed.activations.length === 0 ? (
        <Empty title="No accessible activations">
          <p>
            {named !== null && named > 0
              ? `${named} ${named === 1 ? "activation exists" : "activations exist"}, but this account cannot open them.`
              : "No activation has been added for this client."}
          </p>
        </Empty>
      ) : null}
      {listed.status === "read" && listed.activations.length > 0 ? (
        <div className="tablewrap">
          <div className="tablescroll">
            <table>
              <thead>
                <tr>
                  <th scope="col">Activation</th>
                  <th scope="col">Venue</th>
                  <th scope="col">When</th>
                  <th scope="col">State</th>
                  <th scope="col">
                    <span className="vh">Open</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {listed.activations.map((activation) => {
                  const heading = activationName(activation.name);
                  const run = runOf(activation);
                  const venue = said(activation.venue);
                  return (
                    <tr key={activation.activation_id}>
                      <td data-label="Activation">
                        <p className={heading.unnamed ? "t-id quiet" : "t-id"}>{heading.text}</p>
                      </td>
                      <td data-label="Venue">{venue === "" ? <span className="unk">not recorded</span> : venue}</td>
                      <td data-label="When" className="num">
                        {run === "" ? <span className="unk">not recorded</span> : run}
                      </td>
                      <td data-label="State">
                        <span className={`tag ${STATE_TAG[lifecycle(activation)]}`}>{lifecycle(activation)}</span>
                      </td>
                      <td data-label="">
                        <button
                          type="button"
                          className="btn btn-quiet btn-sm"
                          onClick={() => onOpen(activation.activation_id)}
                        >
                          Open
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
      {listed.status === "read" && listed.activations.length > 0 ? (
        disagree ? (
          <p className="help help-head">
            {visible !== null && named !== null && visible > named
              ? `This directory lists ${visible} activations and the company record names ${named}. The extras are shows this sign-in can open.`
              : `This account can open ${visible} of ${named} activations. The others are not listed.`}
          </p>
        ) : null
      ) : null}
    </section>
  );
}

function runOf(activation: ActivationLine): string {
  const opensOn = said(activation.opens_on);
  const closesOn = said(activation.closes_on);
  if (opensOn === "" || closesOn === "") return "";
  return opensOn === closesOn ? opensOn : `${opensOn} to ${closesOn}`;
}

const said = (value: string | null | undefined): string => (typeof value === "string" ? value.trim() : "");

/**
 * What may leave, and where the leaving is done.
 *
 * The design pairs this sentence with a Produce a file button, disabled while
 * there is no agreement, whose handler says producing is done from an activation
 * rather than from here. A control whose only outcome is a sentence explaining
 * that it is the wrong control is the defect the header of this file already
 * argues against, so the sentence is drawn and the button is not.
 */
function MayBeProduced({ client }: { readonly client: LiveClient }): ReactNode {
  const none = client.agreement.state === "none";
  return (
    <section>
      <SecLabel info="Exports are created from an activation's Data page, not from the client record.">
        Data export
      </SecLabel>
      <div className="panel panel-stack">
        <p>
          {none
            ? "Personal-data exports are unavailable until a processing agreement is recorded."
            : "Personal-data exports are available for the purposes named in the agreement. Each export is recorded."}
        </p>
      </div>
    </section>
  );
}

/**
 * The processing agreement, recorded.
 *
 * THIS IS THE CONTROL THAT PERMITS AN EXPORT, and it is worth saying plainly
 * because it does not look like one. Nothing on this panel produces a file;
 * what it does is record that a signed agreement exists, and the service refuses
 * to produce anything for a client where it does not. The copy says so, because
 * an admin who thinks this is a filing cabinet will not understand why an export
 * three screens away is failing.
 *
 * There is no control to remove one. api/src/admin/people.ts carries the
 * argument: a button that quietly un-permitted future exports while leaving the
 * files already produced in existence would look like it had dealt with a
 * terminated agreement and would have done nothing about the part that matters.
 *
 * WHAT IS ON FILE IS READ IN The record AND NOT AGAIN HERE. It was drawn twice —
 * a `.dl` on this panel and nothing above it — and the design's shape is one
 * record at the top of the page with the writes under it. Two copies of the same
 * four facts on one screen is two things to keep in step for no reader's benefit.
 */
function Agreed({
  client,
  api,
  token,
  onSessionEnded,
  onRecorded,
}: {
  readonly client: LiveClient;
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly onSessionEnded: (detail: string) => void;
  readonly onRecorded: () => void;
}): ReactNode {
  const { agreement } = client;
  const [signedOn, setSignedOn] = useState(agreement.signedOn ?? "");
  const [months, setMonths] = useState(agreement.retentionMonths === null ? "" : String(agreement.retentionMonths));
  const [person, setPerson] = useState(agreement.noticesPerson ?? "");
  const [email, setEmail] = useState(agreement.noticesEmail ?? "");
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<AdminRefusal | null>(null);
  const [done, setDone] = useState(false);

  const submit = async (): Promise<void> => {
    setBusy(true);
    setRefusal(null);
    setDone(false);

    // Sent as typed, including a retention that is not a number: the service
    // owns these rules and answers with a sentence. Number("") is 0, which the
    // service refuses as a period, so an empty box gets the same refusal a zero
    // does rather than a different one invented here.
    const result = await recordAgreement(api, token, client.clientId, {
      signedOn: signedOn.trim(),
      retentionMonths: Number(months.trim()),
      noticesPerson: person.trim() === "" ? null : person.trim(),
      noticesEmail: email.trim() === "" ? null : email.trim(),
    });
    setBusy(false);

    if (result.ok) {
      setDone(true);
      onRecorded();
      return;
    }

    setRefusal(result);
    if (result.kind === "session_ended") onSessionEnded(result.detail);
  };

  return (
    <section>
      <SecLabel info="Record the signed agreement that allows personal-data exports for this client.">
        Processing agreement
      </SecLabel>
      <div className="panel panel-stack">

        {!done ? null : (
          <div className="notice notice-good" role="status">
            <h3>The agreement is on file</h3>
            <p>Personal-data exports are now available from each activation&rsquo;s Data page.</p>
          </div>
        )}
        {refusal === null ? null : <AdminRefused refusal={refusal} />}

        <FieldRow label="Signed on" help="The date on the document.">
          <input
            className="input"
            type="date"
            value={signedOn}
            onChange={(event) => setSignedOn(event.target.value)}
            disabled={busy}
          />
        </FieldRow>
        <FieldRow
          label="Retention period, in months"
          help="What the agreement says is kept. An agreement has to name one."
        >
          <input
            className="input"
            inputMode="numeric"
            value={months}
            onChange={(event) => setMonths(event.target.value)}
            disabled={busy}
          />
        </FieldRow>
        <FieldRow label="Named contact">
          <input
            className="input"
            value={person}
            onChange={(event) => setPerson(event.target.value)}
            disabled={busy}
          />
        </FieldRow>
        <FieldRow
          label="Address for notices"
          help="The contract contact. This does not need to be a Console user."
        >
          <input
            className="input"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            disabled={busy}
          />
        </FieldRow>
        <div className="acts acts-flush">
          <button type="button" className="btn" onClick={() => void submit()} disabled={busy}>
            {busy
              ? "Recording…"
              : agreement.state === "recorded"
                ? "Record this agreement"
                : "Put an agreement on file"}
          </button>
        </div>
      </div>
    </section>
  );
}

function CorrectName({
  client,
  api,
  token,
  onSessionEnded,
  onCorrected,
}: {
  readonly client: LiveClient;
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly onSessionEnded: (detail: string) => void;
  readonly onCorrected: () => void;
}): ReactNode {
  const [name, setName] = useState(client.name);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<AdminRefusal | null>(null);
  const [corrected, setCorrected] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    setBusy(true);
    setRefusal(null);

    const result = await correctClient(api, token, client.clientId, name);
    setBusy(false);

    if (result.ok) {
      setCorrected(result.value.name);
      setName(result.value.name);
      onCorrected();
      return;
    }

    setRefusal(result);
    if (result.kind === "session_ended") onSessionEnded(result.detail);
  };

  return (
    <section>
      <SecLabel>Name</SecLabel>
      <div className="panel panel-stack">
        {corrected === null ? null : (
          <div className="notice notice-good" role="status">
            <h3>Name changed to {corrected}</h3>
            <p>New activations will use this spelling.</p>
          </div>
        )}
        {refusal === null ? null : <AdminRefused refusal={refusal} />}
        <FieldRow label="The client&rsquo;s name">
          <input
            className="input"
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={busy}
          />
        </FieldRow>
        <div className="acts acts-flush">
          <button type="button" className="btn" onClick={() => void submit()} disabled={busy}>
            {busy ? "Saving…" : "Save name"}
          </button>
        </div>
      </div>
    </section>
  );
}

type Peopled =
  | { readonly status: "reading" }
  | { readonly status: "read"; readonly identities: readonly LiveIdentity[] }
  | { readonly status: "failed"; readonly refusal: AdminRefusal };

type PersonDraft = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  jobTitle: string;
};

const emptyDraft = (): PersonDraft => ({
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  jobTitle: "",
});

function personHeading(identity: LiveIdentity): string {
  const name = [identity.firstName, identity.lastName].filter((part) => part !== null && part !== "").join(" ");
  return name === "" ? identity.email : name;
}

/**
 * The people at this company who can be named as having instructed something.
 *
 * WHY THIS IS ON THE CLIENT AND NOT ON REGISTRATION. A person belongs to a
 * company rather than to a show. The refusal on Registration says what to do;
 * this is where it is done.
 *
 * ALWAYS A CLIENT VIEWER. 006 gives a nova_admin no client, so a Nova person
 * created here could never be the instructor. There is nothing for a picker
 * to pick.
 *
 * A TABLE RATHER THAN THE CONSOLE'S OLDER TILES. `.console-directory` is a
 * flex list written against the `--xp-*` scale and shared with Choose.tsx; a
 * list of people with a title and a telephone against each is columns, which is
 * what the design draws people as. It folds on `data-label` below 760px, so the
 * narrow reading is stacked blocks rather than a row scrolling sideways.
 */
function People({
  clientId,
  api,
  token,
  onSessionEnded,
}: {
  readonly clientId: string;
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly onSessionEnded: (detail: string) => void;
}): ReactNode {
  const [listed, setListed] = useState<Peopled>({ status: "reading" });
  const [generation, setGeneration] = useState(0);
  const [draft, setDraft] = useState<PersonDraft>(emptyDraft);
  const [editing, setEditing] = useState<LiveIdentity | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<AdminRefusal | null>(null);
  const [added, setAdded] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const result = await listIdentities(api, token, clientId);
      if (cancelled) return;

      if (result.ok) {
        setListed({ status: "read", identities: result.value.identities });
        return;
      }

      setListed({ status: "failed", refusal: result });
      if (result.kind === "session_ended") onSessionEnded(result.detail);
    })();

    return () => {
      cancelled = true;
    };
  }, [api, token, clientId, onSessionEnded, generation]);

  const close = (): void => {
    setOpen(false);
    setEditing(null);
    setDraft(emptyDraft());
  };

  const submit = async (): Promise<void> => {
    setBusy(true);
    setRefusal(null);

    const profile = {
      firstName: draft.firstName,
      lastName: draft.lastName,
      phone: draft.phone,
      jobTitle: draft.jobTitle,
    };

    const result =
      editing === null
        ? await createIdentity(api, token, {
            email: draft.email,
            role: "client_viewer",
            clientId,
            ...profile,
          })
        : await correctIdentity(api, token, editing.identityId, profile);
    setBusy(false);

    if (result.ok) {
      setAdded(result.value.email);
      close();
      // Read the list again rather than pushing the new row onto it. The
      // service decides what is on the books; a row appended here would be this
      // screen's opinion, and it would survive a refusal that happened after it.
      setGeneration((n) => n + 1);
      return;
    }

    setRefusal(result);
    if (result.kind === "session_ended") onSessionEnded(result.detail);
  };

  return (
    <section>
      <SecLabel info="These people can approve registration setup. Adding someone does not grant Console access.">
        Client contacts
      </SecLabel>

      {added === null ? null : (
        <div className="notice notice-good" role="status">
          <h3>{added} added</h3>
          <p>They can now be selected in Registration setup.</p>
        </div>
      )}
      {refusal === null || open ? null : <AdminRefused refusal={refusal} />}

      {listed.status === "reading" ? (
        <p className="help help-tight">Loading contacts…</p>
      ) : listed.status === "failed" ? (
        <AdminRefused refusal={listed.refusal} />
      ) : listed.identities.length === 0 ? (
        // The pack's empty card, dashed rather than solid, because there is
        // nothing in it: a successful read of an empty list, not a panel that
        // failed to fill.
        <Empty title="No client contacts yet">
          <p>Add someone before saving Registration setup.</p>
        </Empty>
      ) : (
        <div className="tablewrap">
          <div className="tablescroll">
            <table>
              <thead>
                <tr>
                  <th scope="col">Person</th>
                  <th scope="col">Title</th>
                  <th scope="col">Telephone</th>
                  <th scope="col">
                    <span className="vh">Edit</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {listed.identities.map((identity) => {
                  const heading = personHeading(identity);
                  return (
                    <tr key={identity.identityId}>
                      <td data-label="Person">
                        {heading}
                        {heading === identity.email ? null : (
                          <p className="t-note mono">{identity.email}</p>
                        )}
                      </td>
                      <td data-label="Title">
                        {identity.jobTitle ?? <span className="quiet">not recorded</span>}
                      </td>
                      <td data-label="Telephone">
                        {identity.phone ?? <span className="quiet">not recorded</span>}
                      </td>
                      <td data-label="">
                        <button
                          type="button"
                          className="btn btn-quiet btn-sm"
                          onClick={() => {
                            setEditing(identity);
                            setDraft({
                              firstName: identity.firstName ?? "",
                              lastName: identity.lastName ?? "",
                              email: identity.email,
                              phone: identity.phone ?? "",
                              jobTitle: identity.jobTitle ?? "",
                            });
                            setRefusal(null);
                            setOpen(true);
                          }}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="acts">
        <button
          type="button"
          className="btn"
          onClick={() => {
            setEditing(null);
            setDraft(emptyDraft());
            setRefusal(null);
            setOpen(true);
          }}
        >
          Add person
        </button>
      </div>

      {open ? (
        <Dialog
          title={editing === null ? "Add person" : "Edit person"}
          onClose={close}
          footer={
            <>
              <button type="button" className="btn btn-quiet" onClick={close} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="btn" onClick={() => void submit()} disabled={busy}>
                {busy ? (editing === null ? "Adding…" : "Saving…") : editing === null ? "Add this person" : "Save this person"}
              </button>
            </>
          }
        >
          {refusal === null ? null : <AdminRefused refusal={refusal} />}
          <FieldRow label="Their first name">
            <input
              className="input"
              name="new-identity-first-name"
              value={draft.firstName}
              onChange={(event) => setDraft({ ...draft, firstName: event.target.value })}
              disabled={busy}
            />
          </FieldRow>
          <FieldRow label="Their last name">
            <input
              className="input"
              name="new-identity-last-name"
              value={draft.lastName}
              onChange={(event) => setDraft({ ...draft, lastName: event.target.value })}
              disabled={busy}
            />
          </FieldRow>
          <FieldRow label="Their email address">
            <input
              className="input"
              type="email"
              name="new-identity-email"
              value={draft.email}
              onChange={(event) => setDraft({ ...draft, email: event.target.value })}
              disabled={busy || editing !== null}
            />
          </FieldRow>
          <FieldRow label="Telephone (optional)">
            <input
              className="input"
              type="tel"
              name="new-identity-phone"
              value={draft.phone}
              onChange={(event) => setDraft({ ...draft, phone: event.target.value })}
              disabled={busy}
            />
          </FieldRow>
          <FieldRow label="Title (optional)">
            <input
              className="input"
              name="new-identity-title"
              value={draft.jobTitle}
              onChange={(event) => setDraft({ ...draft, jobTitle: event.target.value })}
              disabled={busy}
            />
          </FieldRow>
        </Dialog>
      ) : null}
    </section>
  );
}

function Archive({
  client,
  api,
  token,
  onSessionEnded,
  onArchived,
}: {
  readonly client: LiveClient;
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly onSessionEnded: (detail: string) => void;
  readonly onArchived: () => void;
}): ReactNode {
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<AdminRefusal | null>(null);
  // The design asks first (console-clients.jsx, ClArchive; audit item 8).
  const [asking, setAsking] = useState(false);

  const submit = async (): Promise<void> => {
    setAsking(false);
    setBusy(true);
    setRefusal(null);

    const result = await archiveClient(api, token, client.clientId);
    setBusy(false);

    if (result.ok) {
      onArchived();
      return;
    }

    setRefusal(result);
    if (result.kind === "session_ended") onSessionEnded(result.detail);
  };

  return (
    <section>
      <SecLabel>Archive</SecLabel>
      <div className="panel panel-stack">
        {refusal === null ? null : <AdminRefused refusal={refusal} />}
        <div className="acts acts-flush">
          <button type="button" className="btn btn-quiet" onClick={() => setAsking(true)} disabled={busy}>
            {busy ? "Archiving…" : "Archive this client"}
          </button>
        </div>
      </div>
      {asking ? (
        <Confirm
          title={`Archive ${client.name}?`}
          verb="Archive this client"
          danger
          onCancel={() => setAsking(false)}
          onConfirm={() => void submit()}
        >
          <p>Events already captured stay under this row.</p>
        </Confirm>
      ) : null}
    </section>
  );
}

/**
 * What a company's row does not hold, said rather than drawn as empty fields.
 *
 * 010 put one `name` column on `client`, and the same call Registration.tsx
 * makes about a disabled control that looks like it works applies here: a row of
 * blank boxes headed "Logo" is a screen a client concludes is configured.
 *
 * The first sentence is narrower than it was. Named contact and address for
 * notices are on the record above now, because 126 stores both — on the
 * agreement rather than on the company — and a page that draws them and then
 * says contacts are not stored is a page arguing with itself.
 */
function NotOnThisRow(): ReactNode {
  return (
    <section>
      <SecLabel>Not on this row</SecLabel>
      <div className="panel panel-stack">
        <p>
          A general contact name and email are not stored on this row. The named contact and the
          address for notices belong to the processing agreement rather than to the company, and they
          are who a notice about that agreement goes to. A postal address is not stored. Client legal
          copy is not stored on this client. Logo, brand colours and brand font are not stored yet.
        </p>
        <ul className="reasons">
          {LEGAL.map((name) => (
            <li key={name}>{name}: not stored on this client.</li>
          ))}
          <li>Company number and jurisdiction: not stored yet.</li>
          <li>A default timezone for new shows: not stored yet.</li>
          <li>Who may view is a grant, not a field on this company.</li>
          <li>Billing stays on admin.novaexhibits.com.</li>
        </ul>
      </div>
    </section>
  );
}

const LEGAL = ["Terms and conditions", "Privacy policy", "Cookie policy", "Rules"] as const;
